import dayjs from 'dayjs';

import { DEFAULT_DATE_FORMAT, DEFAULT_TIME_FORMAT, TIME_FORMAT_PATTERNS } from '../utils/dateDisplay';
import { DEFAULT_APPEARANCE, DEFAULT_THEME, THEMES } from './themes';
import { PLATFORM_TIMEZONE } from './timezones';

// Registry driving the Preferences modal AND client-side defaults. Adding a
// preference = one entry here plus a matching PREF_REGISTRY entry in
// keepr-api utils/preferenceUtils.js (the server allowlists keys).
//
// Controls: 'select' (options: [{value, label}]), 'segmented' (the same
// shape, rendered as a joined switcher), 'switch' (a real yes/no, drawn as the
// platform's ToggleRow; a preference never set reads as this entry's
// `default`), 'theme' (the palette picker, which draws its own swatches from
// the theme catalog), 'timezone' (a searchable zone picker that can be
// cleared, with the device's zone one click away) and 'currency' (a
// searchable currency picker that can be cleared). Add control types as
// preferences need them — and add
// the CONTROL rather than dressing a boolean up as a Yes/No select.
//
// `surface` says WHERE a preference is edited. Without one, the Preferences
// modal renders it. With one, the page named owns the control and the modal
// leaves it alone — the entry is still required here, because this file is
// half of the contract (the client's defaults; the server's PREF_REGISTRY is
// the other half), and a preference the client has never heard of reads as
// undefined on every surface that asks for it.

// A fixed sample date whose day > 12 disambiguates M/D vs D/M orderings.
const SAMPLE_DATE = '2025-03-31';

// An afternoon time, so the two clock choices actually read differently.
const SAMPLE_TIME = '2025-03-31T13:30:00';

export const DATE_FORMAT_CHOICES = [
	'MMM D, YYYY',
	'YYYY-MM-DD',
	'M/D/YYYY',
	'D/M/YYYY',
	'DD-MMM-YYYY',
];

export const TIME_FORMAT_CHOICES = [ '12h', '24h' ];

// How measurement values read. 'default' follows the card's own display
// rules; a system swaps the display unit for the measure's system default
// (utils/units registry `systemDefaults`) and skips the element's threshold
// rules (docs/plans/measurements.md decision #9). The API ignores it: stored
// values, display titles and exports are shared, so they always read as the
// card defines them.
export const UNIT_SYSTEM_CHOICES = [
	{ value: 'default', label: 'As defined on the card' },
	{ value: 'metric', label: 'Metric' },
	{ value: 'imperial', label: 'Imperial' },
];
export const DEFAULT_UNIT_SYSTEM = 'default';

// keepr's currency when nobody has chosen one (keepr-api
// utils/newMoneyCurrency.js KEEPR_CURRENCY): the one the card editor always
// started new money in.
export const KEEPR_CURRENCY = 'USD';

// The one group that says how keepr reads to this person — the owner's
// "Language & region" (docs/plans/localization-preferences.md, ruled L4).
export const REGION_GROUP = 'Language & region';

// Light, dark, or whatever the machine is set to. 'system' is the default
// because a person who has already told their OS which they want should not
// have to tell keepr a second time.
export const APPEARANCE_CHOICES = [
	{ value: 'system', label: 'System' },
	{ value: 'light', label: 'Light' },
	{ value: 'dark', label: 'Dark' },
];

// The palette picker's options. Values come from the theme catalog so a
// theme added there shows up here without a second edit.
export const THEME_CHOICES = THEMES.map(theme => ({
	value: theme.key,
	label: theme.label,
	description: theme.blurb,
}));

export const PREFERENCE_DEFS = [
	{
		key: 'theme',
		group: 'Appearance',
		label: 'Theme',
		description: 'The palette the whole app wears. Each one has a light and a dark version.',
		control: 'theme',
		default: DEFAULT_THEME,
		options: THEME_CHOICES,
	},
	{
		key: 'appearance',
		group: 'Appearance',
		label: 'Light or dark',
		description: 'System follows your device, and changes with it.',
		control: 'segmented',
		default: DEFAULT_APPEARANCE,
		options: APPEARANCE_CHOICES,
	},
	{
		key: 'dateFormat',
		group: REGION_GROUP,
		label: 'Date format',
		description: 'How dates are displayed throughout the app.',
		control: 'select',
		default: DEFAULT_DATE_FORMAT,
		options: DATE_FORMAT_CHOICES.map(format => ({
			value: format,
			label: dayjs(SAMPLE_DATE).format(format),
		})),
	},
	{
		key: 'timeFormat',
		group: REGION_GROUP,
		label: 'Time format',
		description: 'How times of day are displayed, and how time pickers read.',
		control: 'select',
		default: DEFAULT_TIME_FORMAT,
		// The VALUE is the abstract clock ('12h'/'24h'), not a pattern: the
		// preference drives pickers and formatters that each need their own
		// format string. See utils/dateDisplay.js.
		options: TIME_FORMAT_CHOICES.map(value => ({
			value,
			label: dayjs(SAMPLE_TIME).format(TIME_FORMAT_PATTERNS[value]),
		})),
	},
	{
		// The person's ONE zone (ruled L1): a new top-level collection they
		// create is born in it (keepr-api utils/preferenceUtils.js
		// stampNewCollection), and quiet hours will read it when they are
		// built. Empty means "never told": the collection then inherits
		// keepr's default, and nothing writes it behind the person's back —
		// the control offers the device's zone, it never takes it.
		key: 'timezone',
		group: REGION_GROUP,
		label: 'Time zone',
		description: 'New collections you create start in this time zone.',
		control: 'timezone',
		default: '',
		unsetLabel: `Not set — keepr's default, ${PLATFORM_TIMEZONE}`,
	},
	{
		// The currency new money starts in where the collection sets none
		// (ruled L3; utils/newMoneyCurrency.js), and what a new top-level
		// collection's own currency is born as. Empty = keepr's USD.
		key: 'currency',
		group: REGION_GROUP,
		label: 'Currency',
		description: 'New currency elements start in this currency when the collection does not set one. New collections you create start with it too.',
		control: 'currency',
		default: '',
		unsetLabel: 'Not set — US dollar',
	},
	{
		key: 'unitSystem',
		group: REGION_GROUP,
		label: 'Units',
		description: 'How measurements are displayed: as the card defines them, or converted to metric or imperial.',
		control: 'select',
		default: DEFAULT_UNIT_SYSTEM,
		options: UNIT_SYSTEM_CHOICES,
	},
	{
		// What heads Home in place of the time-of-day greeting (KPR-1122;
		// keepr-api PREF_REGISTRY homeTitle: trimmed, 1-60 characters, plain
		// text). Empty = the greeting. Also edited from Home's header ⋯.
		key: 'homeTitle',
		group: 'Home',
		label: 'Home title',
		description: 'Shown at the top of Home instead of the greeting. Leave it empty to be greeted by the time of day.',
		control: 'text',
		default: '',
		maxLength: 60,
	},
	{
		// The email master, RETIRING. It has been in userPreferences since the
		// notify action shipped; /account/notifications gave it its first UI
		// and now writes the `notificationSettings` overlay instead (services/
		// notifications.js, setMasterChannel). This entry is what still READS
		// it, for one release: the API's email channel module falls back to it
		// until the `retireNotificationEmails` migration backfills the settings
		// rows, and this goes when that lands.
		key: 'notificationEmails',
		group: 'Notifications',
		surface: 'notifications',
		label: 'Email',
		description: 'Notification emails from every collection you are in.',
		control: 'toggle',
		default: true,
	},
	{
		// Whether a change to an item the person FOLLOWS is mailed as well as
		// put in the inbox (keepr-api docs/SCHEMA.md § Following an item,
		// KPR-391). Its own key, like exportEmails: following a dozen town
		// reports is not the same wish as hearing about a med round. Edited on
		// Settings › Notifications beside the list of followed items, not in
		// the modal. An opt-OUT — absent means on, on both sides of the
		// contract; the account's Email master still silences it.
		key: 'itemFollowEmails',
		group: 'Notifications',
		surface: 'notifications',
		label: 'Email me when items I follow change',
		description: 'Off: changes to items you follow reach your inbox only.',
		control: 'toggle',
		default: true,
	},
	{
		// Whether a stored export may email this person (keepr-api
		// utils/export/exportDelivery.js). Its own key rather than
		// notificationEmails: an export is a report someone set up for you,
		// not a notification, and silencing one must not silence the other.
		// An opt-OUT — absent means on, on both sides of the contract. There
		// is deliberately no RCS twin: no messaging provider exists.
		key: 'exportEmails',
		group: 'Email',
		label: 'Export emails',
		description: 'Emails from saved exports that list you as a recipient, each with a download link. Off: you are skipped.',
		control: 'switch',
		default: true,
	},
];

export const PREFERENCE_DEFAULTS = Object.fromEntries(
	PREFERENCE_DEFS.map(def => [def.key, def.default])
);
